"use client";

import type { IImage } from "@components/types/media";
import type { ICmsFloatingMediaItem } from "@mw/shared/payload-types";
import type React from "react";

import { Image } from "@components/core/Image";
import { PreviewPlayerButton, PreviewPlayerProgressRing, useVideoPlaybackState } from "@components/core/PreviewPlayer";
import { isValidImage } from "@ui/lib/image";
import { clamp, lerp } from "@ui/lib/math";
import { mediaQueries } from "@ui/lib/screens";
import { sizes } from "@ui/lib/sizes";
import { getVideoUrl } from "@utilities/payload";
import { clsx } from "clsx";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

// Scatter slots transcribed from the Figma "Big Carousel" frame (1728×915), as percentages of
// the desktop container so the arrangement scales with its width. Each video fills the slot at
// its CMS array index (capped at this count via the block's maxRows).
const SLOTS: { left: number; top: number; width: number; height: number }[] = [
  { left: 38.48, top: 13.83, width: 5.79, height: 13.88 },
  { left: 17.65, top: 20.82, width: 10.42, height: 24.81 },
  { left: 42.53, top: 42.13, width: 14.47, height: 34.43 },
  { left: 59.09, top: 22.9, width: 5.79, height: 13.88 },
  // Nudged right of its Figma position: its full-size box keeps the same 0.47% margin to the
  // left screen edge that the rightmost slot keeps to the right one.
  { left: 4.81, top: 52.4, width: 5.79, height: 13.88 },
  // The two top-right slots are nudged left of their Figma positions: the rightmost so its
  // full-size box stays inside the window, and the pair so their full-size boxes clear each
  // other (centres ≥ MAX_W apart) without colliding with the slot below-left.
  { left: 89.4, top: 32.95, width: 5.79, height: 13.88 },
  { left: 72, top: 11.64, width: 10.42, height: 24.81 },
  { left: 71.18, top: 55.14, width: 10.42, height: 24.81 },
];

// Hover-resize: every card is laid out at the largest slot size and cropped down with a
// clip-path inset, so resizing only moves the crop window — never the media inside. The 24px
// radius is part of the clip shape. `--fm-t` is the per-card size factor: 0 = smallest slot
// size, 1 = largest.
const MAX_W = Math.max(...SLOTS.map(slot => slot.width));
const MIN_W = Math.min(...SLOTS.map(slot => slot.width));
const MAX_H = Math.max(...SLOTS.map(slot => slot.height));
const MIN_H = Math.min(...SLOTS.map(slot => slot.height));
// The scatter keeps the 1728/915 frame ratio, so vertical percentages convert to cqw and all the
// crop math stays in one container-relative unit (cqh would fall back to viewport units — the
// root is an inline-size container).
const PERCENT_HEIGHT_IN_CQW = 915 / 1728;
// Crop inset per side at t=0. Symmetric, so cards shrink around their centre-anchored slot point.
const INSET_X_CQW = (MAX_W - MIN_W) / 2;
const INSET_Y_CQW = ((MAX_H - MIN_H) / 2) * PERCENT_HEIGHT_IN_CQW;
// Per-side crop inset, easing to zero at t=1; negate the input for the badge's counter-shift.
const cropInset = (cqw: number) => `calc((1 - var(--fm-t)) * ${cqw}cqw)`;
const CARD_CLIP = `inset(${cropInset(INSET_Y_CQW)} ${cropInset(INSET_X_CQW)} round 24px)`;
const BADGE_SHIFT_X = cropInset(-INSET_X_CQW);
const BADGE_SHIFT_Y = cropInset(-INSET_Y_CQW);
// The media inside shrinks with the crop, though less than the window itself. Hard ceiling
// ~0.59: the fully-cropped window is ~40% of the box, so past that the scaled-down media would
// expose gaps at the clip edges (both shrink linearly in t, so the endpoints bound every t).
const MEDIA_SHRINK = 0.45;
const MEDIA_SCALE = `calc(1 - (1 - var(--fm-t)) * ${MEDIA_SHRINK})`;
// Max drift toward the cursor as a fraction of the card box, in cqw for both axes. The media
// layer bleeds 5% past the box (keep in sync with the -tw-inset-[5%] class) so the
// counter-shift can never expose a gap.
const CARD_DRIFT = 0.035;
const CARD_DRIFT_X_CQW = CARD_DRIFT * MAX_W;
const CARD_DRIFT_Y_CQW = CARD_DRIFT * MAX_H * PERCENT_HEIGHT_IN_CQW;
// The drift is card-local: full strength while the cursor is on the card's visible window, then
// a Gaussian falloff over this many card-widths beyond its edges — cards elsewhere in the
// scatter don't react.
const CARD_DRIFT_REACH = 0.5;
// Card edges: a backdrop-blur overlay masked by two gradients (opaque at the edges, clear in
// the middle — the layers union), fading the blur in toward every border with a 15% falloff per
// side. Inset by the same calc as the clip so it always hugs the visible (cropped) edges.
const EDGE_BLUR_FADE = "black, transparent 15%, transparent 85%, black";
const EDGE_BLUR_MASK = `linear-gradient(to right, ${EDGE_BLUR_FADE}), linear-gradient(to bottom, ${EDGE_BLUR_FADE})`;
const EDGE_BLUR_INSET = `${cropInset(INSET_Y_CQW)} ${cropInset(INSET_X_CQW)}`;
// Gaussian width of the cursor's influence, as a fraction of the container width: a card at
// this distance is at ~60% influence, fading smoothly to nothing with no hard boundary.
const RESIZE_FALLOFF = 0.15;
// Exponential-smoothing time constant (ms) of each card's size easing toward its target — the
// low-pass that makes the response glide behind the cursor instead of tracking it eagerly.
const RESIZE_SMOOTHING = 260;
// Time constant (ms) of the hover field fading in/out over the resting composition, so crossing
// the section edge blends gradually instead of reading as a mode flip.
const HOVER_BLEND_SMOOTHING = 450;
// While a playing card is hovered (pinned to max size), every other card's size target scales
// down by this fraction. Multiplicative, so a card already at minimum (factor 0) stays put.
const PINNED_OTHERS_SHRINK = 0.35;
// The playing clip's volume is slaved to its card's live size factor: full at max size, decaying
// to a near-silent floor (kept above zero so the clip reads as playing, not stopped). The
// exponent makes the sound fall off much harder than the card shrinks.
const VOLUME_MIN = 0.03;
const VOLUME_EXPONENT = 3;

// A slot's designed size expressed as its position along the min→max size range — the card's
// crop at rest, restored when the pointer leaves. (All slots share ~the same aspect ratio, so
// the width-derived factor reproduces the designed heights too.)
const getSlotRestT = (slot: (typeof SLOTS)[number]) => (slot.width - MIN_W) / (MAX_W - MIN_W);

// The playing clip's volume mapped from its live size factor. Clamped because video.volume throws
// outside [0, 1].
const volumeForFactor = (factor: number) => VOLUME_MIN + (1 - VOLUME_MIN) * clamp(factor) ** VOLUME_EXPONENT;

// Mirrors the rendered card widths: 2-up on mobile, 3-up from md, scatter slots at lg.
const COVER_SIZES = sizes("50vw", { md: "33vw", lg: "15vw" });

type FloatingMediaProps = {
  title?: string | null;
  description?: string | null;
  videos?: ICmsFloatingMediaItem;
};

// At most one clip plays at a time; an ended clip is already paused, so !paused alone finds it.
const findPlayingVideo = (list: HTMLElement | null) =>
  Array.from(list?.querySelectorAll("video") ?? []).find(video => !video.paused) ?? null;

export function FloatingMedia({ title, description, videos }: FloatingMediaProps) {
  // Keep each card's CMS array index as its scatter slot, so an unresolvable video leaves its
  // slot empty instead of shifting later cards into earlier Figma positions.
  const cards = (videos ?? []).flatMap((item, slotIndex) => {
    const slot = SLOTS[slotIndex];
    const videoUrl = getVideoUrl(item.media);
    if (!slot || !videoUrl) return [];
    return [
      {
        slotIndex,
        slot,
        restT: getSlotRestT(slot),
        videoUrl,
        cover: { resource: item.coverImage, sizes: COVER_SIZES },
      },
    ];
  });

  const listRef = useRef<HTMLUListElement>(null);
  // The last known cursor position — kept after the pointer leaves, so the swell deflates in
  // place during the fade-out instead of snapping to a pointless target.
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const isHoveredRef = useRef(false);
  // How much of the hover field is currently applied (0 = resting composition, 1 = full
  // distance map), eased toward 0/1 with its own time constant.
  const hoverBlend = useRef(0);
  const resizeFrame = useRef(0);
  const lastStepTime = useRef(0);
  // Each card's live size factor, low-pass filtered toward its target — initialised lazily to
  // the slot's rest size.
  const sizeFactors = useRef<number[]>([]);
  const cardDrifts = useRef<{ x: number; y: number }[]>([]);

  useEffect(() => () => cancelAnimationFrame(resizeFrame.current), []);

  // The clip that was playing when the section scrolled out of view, to resume on re-entry.
  const resumeVideoRef = useRef<HTMLVideoElement | null>(null);
  const isVisibleRef = useRef(false);

  // Playback stops while the section is off screen — a plain pause keeping the position, so
  // scrolling back resumes where it left off.
  useEffect(() => {
    const list = listRef.current;

    if (!list) return;

    const observer = new IntersectionObserver(([entry]) => {
      isVisibleRef.current = entry.isIntersecting;

      if (entry.isIntersecting) {
        const resuming = resumeVideoRef.current;

        if (resuming) {
          void resuming
            .play()
            .then(() => {
              // play() resolves a frame or two later. Keep the clip playing only if the section is
              // still on screen AND this is still the queued resume target; if it scrolled back off
              // or the user started a different card meanwhile (which clears the ref), pause it so a
              // stale resume can neither start off-screen nor hijack the card the user chose. The
              // ref is consumed only on a clean resume — an off-screen pause leaves it queued.
              if (isVisibleRef.current && resumeVideoRef.current === resuming) {
                resumeVideoRef.current = null;
              } else {
                resuming.pause();
              }
            })
            .catch(() => {});
        }
        return;
      }

      const playing = findPlayingVideo(list);

      if (playing) {
        playing.pause();
        resumeVideoRef.current = playing;
      }
    });

    observer.observe(list);

    return () => observer.disconnect();
  }, []);

  // Re-seed the playing clip's volume when the viewport crosses the lg breakpoint: the carousel
  // plays at full volume, the scatter slaves volume to card size. Without this a clip keeps its
  // previous level after the resize loop has settled, until the next play.
  useEffect(() => {
    const lgQuery = window.matchMedia(mediaQueries.lg);

    const syncPlayingVolume = () => {
      const playing = findPlayingVideo(listRef.current);

      if (playing) seedClipVolume(playing);
    };

    lgQuery.addEventListener("change", syncPlayingVolume);

    return () => lgQuery.removeEventListener("change", syncPlayingVolume);
    // seedClipVolume reads only refs and stable module helpers, so the captured closure never goes stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updatePlayingCardLayer(playingVideo: HTMLVideoElement | null) {
    Array.from(listRef.current?.children ?? []).forEach(item => {
      if (!(item instanceof HTMLElement)) return;

      item.style.zIndex = playingVideo && item.contains(playingVideo) ? "10" : "";
    });
  }

  // The playing clip's volume for the current breakpoint: full on the non-lg carousel (no
  // size-linked audio there, which also clears any stale desktop value), or its card's current
  // size factor on the lg scatter, where the resize loop then keeps it in sync.
  function seedClipVolume(video: HTMLVideoElement) {
    if (!window.matchMedia(mediaQueries.lg).matches) {
      video.volume = 1;
      return;
    }

    const index = Array.from(listRef.current?.children ?? []).findIndex(item => item.contains(video));
    video.volume = volumeForFactor(sizeFactors.current[index] ?? cards[index]?.restT ?? 0);
  }

  // Only one card speaks at a time, as in HeroVideoDeck: starting a clip pauses all the others,
  // rewinding them so a later click replays them from the start rather than mid-sentence.
  // Kicks the resize loop so a hovered playing card can swell to max size right away.
  function handleVideoPlay(video: HTMLVideoElement) {
    // A freshly started clip supersedes any other clip queued for scroll-resume. Skip when this
    // IS the queued clip — the observer resumes by calling play(), which also lands here, and its
    // own .then() stays responsible for clearing the ref.
    if (resumeVideoRef.current && resumeVideoRef.current !== video) {
      resumeVideoRef.current = null;
    }

    listRef.current?.querySelectorAll("video").forEach(other => {
      if (other !== video) {
        other.pause();

        if (other.currentTime > 0) {
          other.currentTime = 0;
        }
      }
    });
    updatePlayingCardLayer(video);
    seedClipVolume(video);

    startResizeLoop();
  }

  function handleVideoStop() {
    updatePlayingCardLayer(findPlayingVideo(listRef.current));
    startResizeLoop();
  }

  // One frame of the resize glide: every card's size factor decays exponentially toward its
  // target (Gaussian in cursor distance, or the rest size with no pointer) and is written
  // straight to its CSS var — no React re-render, no CSS transitions. The loop runs only until
  // everything has settled within a hair of its target.
  function stepResize(now: number) {
    resizeFrame.current = 0;

    const list = listRef.current;

    if (!list) return;

    // The size-linked volume only applies on the lg scatter; track it so a viewport that narrows
    // mid-playback hands the clip back to full volume instead of leaving it attenuated.
    const isLg = window.matchMedia(mediaQueries.lg).matches;

    // Clamped so a background-tab gap can't turn into one giant step.
    const elapsed = Math.min(now - lastStepTime.current, 64);
    lastStepTime.current = now;
    const decay = 1 - Math.exp(-elapsed / RESIZE_SMOOTHING);
    const blendDecay = 1 - Math.exp(-elapsed / HOVER_BLEND_SMOOTHING);

    const rect = list.getBoundingClientRect();
    const sigma = rect.width * RESIZE_FALLOFF;
    const point = pointerRef.current;
    let settled = true;

    const blendTarget = isHoveredRef.current ? 1 : 0;

    hoverBlend.current = lerp(hoverBlend.current, blendTarget, blendDecay);

    if (Math.abs(blendTarget - hoverBlend.current) > 0.001) {
      settled = false;
    }

    const items = Array.from(list.children);

    // Exclusive playback means at most one clip can be running; its card drives both the hover
    // pin and the volume below.
    let playingIndex = -1;
    let playingVideo: HTMLVideoElement | null = null;

    for (let index = 0; index < items.length && !playingVideo; index += 1) {
      const video = items[index].querySelector("video");

      if (video && !video.paused) {
        playingIndex = index;
        playingVideo = video;
      }
    }

    // A playing clip only pins to max size while its own card is hovered. Once the cursor
    // leaves, it eases back to the normal distance-based hover scale instead of staying large.
    const pinnedIndex = playingIndex !== -1 && items[playingIndex].matches(":hover") ? playingIndex : -1;

    items.forEach((item, index) => {
      const card = cards[index];

      if (!card || !(item instanceof HTMLElement)) return;

      const { slot, restT } = card;
      const current = sizeFactors.current[index] ?? restT;
      let target = restT;
      let targetX = 0;
      let targetY = 0;

      if (point) {
        const dx = point.x - (rect.left + ((slot.left + slot.width / 2) / 100) * rect.width);
        const dy = point.y - (rect.top + ((slot.top + slot.height / 2) / 100) * rect.height);
        const swell = Math.exp(-(dx * dx + dy * dy) / (2 * sigma * sigma));

        target = lerp(restT, swell, hoverBlend.current);

        // The drift only engages on or near the card: distance is measured from the visible
        // (cropped) window's edges — zero while the cursor is on the card — with a Gaussian
        // falloff beyond them. The direction is soft-clamped relative to the card's own size.
        const visibleW = ((MIN_W + current * (MAX_W - MIN_W)) / 100) * rect.width;
        const visibleH = ((MIN_H + current * (MAX_H - MIN_H)) / 100) * rect.height;
        const edgeX = Math.max(0, Math.abs(dx) - visibleW / 2);
        const edgeY = Math.max(0, Math.abs(dy) - visibleH / 2);
        const edgeDistanceSq = edgeX * edgeX + edgeY * edgeY;
        const reach = visibleW * CARD_DRIFT_REACH;
        const proximity = Math.exp(-edgeDistanceSq / (2 * reach * reach));

        targetX = Math.tanh(dx / visibleW) * proximity * CARD_DRIFT_X_CQW * hoverBlend.current;
        targetY = Math.tanh(dy / visibleH) * proximity * CARD_DRIFT_Y_CQW * hoverBlend.current;
      }

      if (index === pinnedIndex) {
        target = 1;
      } else if (pinnedIndex !== -1) {
        target *= 1 - PINNED_OTHERS_SHRINK;
      }

      const next = lerp(current, target, decay);
      const drift = cardDrifts.current[index] ?? { x: 0, y: 0 };
      const nextX = lerp(drift.x, targetX, decay);
      const nextY = lerp(drift.y, targetY, decay);

      if (Math.abs(target - next) > 0.001 || Math.abs(targetX - nextX) > 0.002 || Math.abs(targetY - nextY) > 0.002) {
        settled = false;
      }

      sizeFactors.current[index] = next;
      cardDrifts.current[index] = { x: nextX, y: nextY };
      item.style.setProperty("--fm-t", next.toFixed(4));
      item.style.setProperty("--fm-dx", `${nextX.toFixed(4)}cqw`);
      item.style.setProperty("--fm-dy", `${nextY.toFixed(4)}cqw`);

      // Slaved to the already-smoothed size factor, so it needs no smoothing of its own. Off the
      // lg scatter the clip plays at full volume.
      if (index === playingIndex && playingVideo) {
        playingVideo.volume = isLg ? volumeForFactor(next) : 1;
      }
    });

    if (!settled) {
      resizeFrame.current = requestAnimationFrame(stepResize);
    }
  }

  function startResizeLoop() {
    if (resizeFrame.current) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // The swell/volume machinery only exists in the lg scatter; without this, a tap-to-play on
    // the mobile carousel (handleVideoPlay kicks the loop too) would set the clip's volume from
    // a size factor the carousel layout never renders — near-silent playback on small slots.
    if (!window.matchMedia(mediaQueries.lg).matches) return;

    lastStepTime.current = performance.now();
    resizeFrame.current = requestAnimationFrame(stepResize);
  }

  function scheduleResize(point: { x: number; y: number } | null) {
    isHoveredRef.current = !!point;

    if (point) {
      pointerRef.current = point;
    }

    startResizeLoop();
  }

  // Keep the heading/copy even if every video fails to resolve; only bail when nothing would render.
  if (!cards.length && !title && !description) return null;

  // One DOM tree serves both breakpoints: below `lg` a horizontal snap-scroll row; at `lg` an
  // absolutely-positioned scatter, with slot percentages fed in as CSS vars consumed only by `lg:`.
  // Negative full-bleed margins break out of the PageSection container so the scatter spans the
  // viewport like the 1728px Figma frame (the section must render without side lines).
  return (
    <div
      className='lg:tw-relative lg:-tw-mx-full-bleed lg:tw-aspect-[1728/915] lg:tw-overflow-hidden lg:tw-@container'
      onMouseLeave={() => scheduleResize(null)}
      onMouseMove={event => scheduleResize({ x: event.clientX, y: event.clientY })}
    >
      {title ? (
        <p
          aria-hidden
          // 8.102cqw = 140px at the 1728px Figma frame, scaling 1:1 with the viewport and
          // capped at 140px (8.75rem) on wider screens. Tracking is in em so it follows the
          // capped font size rather than the viewport.
          className='tw-absolute tw-left-[51.16%] tw-top-[47.87%] tw-hidden -tw-translate-x-1/2 -tw-translate-y-1/2 tw-whitespace-nowrap tw-text-center tw-font-display tw-leading-none tw-tracking-[-0.03em] tw-text-black/10 lg:tw-block lg:tw-text-[min(8.102cqw,8.75rem)]'
        >
          {title}
        </p>
      ) : null}

      {title || description ? (
        <div className='tw-mb-8 tw-flex tw-flex-col tw-gap-4 lg:tw-absolute lg:tw-left-[18.98%] lg:tw-top-[61.26%] lg:tw-w-[19.68%]'>
          {title ? <h3 className='tw-type-2xl tw-text-black lg:tw-type-base'>{title}</h3> : null}
          {/* ch cap keeps the measure constant: the box's %-width keeps scaling with the
              viewport while the font size stays fixed, so without it lines grow ever longer. */}
          {description ? <p className='tw-max-w-[40ch] tw-type-base tw-text-black/60'>{description}</p> : null}
        </div>
      ) : null}

      <ul
        className='-tw-mx-full-bleed tw-flex tw-snap-x tw-snap-mandatory tw-list-none tw-gap-3 tw-overflow-x-auto tw-overscroll-x-contain tw-scroll-smooth tw-scroll-px-full-bleed tw-scrollbar-none tw-px-full-bleed lg:tw-absolute lg:tw-inset-0 lg:tw-m-0 lg:tw-block lg:tw-snap-none lg:tw-overflow-visible lg:tw-scroll-px-0 lg:tw-p-0'
        ref={listRef}
      >
        {cards.map(({ slotIndex, slot, restT, videoUrl, cover }) => (
          <li
            key={slotIndex}
            // Below lg: exactly 2 cards per viewport (3 from md) — a fraction of the list's
            // content box (which is exactly the visible card area) minus the gaps' share (the
            // 0.75rem must stay in sync with tw-gap-3). At lg: anchored by centre and laid out
            // at the largest slot size, with the clip-path cropping down to the current size
            // factor. The clip is gated with min-[64rem], not lg:, because arbitrary properties
            // take no tw- prefix and so are also compiled by the legacy config, whose lg
            // (62.5rem) would clip the carousel cards in the 1000–1023px band.
            className='tw-w-[calc((100%-0.75rem)/2)] tw-shrink-0 tw-snap-start md:tw-w-[calc((100%-1.5rem)/3)] lg:tw-absolute lg:tw-left-[var(--fm-x)] lg:tw-top-[var(--fm-y)] lg:tw-h-[var(--fm-h)] lg:tw-w-[var(--fm-w)] lg:tw-translate-x-[calc(-50%+var(--fm-dx))] lg:tw-translate-y-[calc(-50%+var(--fm-dy))] min-[64rem]:[clip-path:var(--fm-clip)]'
            style={
              {
                "--fm-x": `${slot.left + slot.width / 2}%`,
                "--fm-y": `${slot.top + slot.height / 2}%`,
                "--fm-w": `${MAX_W}%`,
                "--fm-h": `${MAX_H}%`,
                "--fm-t": restT.toFixed(4),
                "--fm-dx": "0cqw",
                "--fm-dy": "0cqw",
                // These reference var(--fm-t), and a custom property resolves its inner var()s
                // where it is DEFINED — so they must live here next to --fm-t, not on the list.
                "--fm-clip": CARD_CLIP,
                "--fm-bx": BADGE_SHIFT_X,
                "--fm-by": BADGE_SHIFT_Y,
                "--fm-ms": MEDIA_SCALE,
              } as React.CSSProperties
            }
          >
            <MediaCard cover={cover} onStop={handleVideoStop} onPlay={handleVideoPlay} videoUrl={videoUrl} />
          </li>
        ))}
      </ul>
    </div>
  );
}

// A raw <video> with the poster as a sibling layer on the exact same box, so both render the
// identical centre crop (the shared vidstack player framed the two differently).
function MediaCard({
  cover,
  onPlay,
  onStop,
  videoUrl,
}: {
  cover: IImage;
  onPlay: (video: HTMLVideoElement) => void;
  onStop: () => void;
  videoUrl: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const t = useTranslations("ClientKeys.Globals");
  const { currentTime, duration, isPlaying, progressAnimationStartedAt } = useVideoPlaybackState(videoRef);
  // The poster hides only while the video is actually rendering frames — and returns on pause or
  // end — so there is never a black flash while the media loads after the play click.
  const [isShowingVideo, setIsShowingVideo] = useState(false);
  const label = isPlaying ? t("pause") : t("play");
  const hasCover = isValidImage(cover);

  function toggleVideo() {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();

    if (event.repeat) {
      return;
    }

    toggleVideo();
  }

  function handleStop() {
    setIsShowingVideo(false);
    onStop();
  }

  return (
    // The 24px radius (tw-rounded-3xl) must stay in sync with CARD_CLIP's `round 24px`.
    <div
      aria-label={label}
      aria-pressed={isPlaying}
      className='tw-group/floating-media-card tw-relative tw-aspect-[4/5] tw-w-full tw-cursor-pointer tw-overflow-hidden tw-rounded-3xl tw-outline-none focus-visible:tw-outline-focus lg:tw-aspect-auto lg:tw-h-full'
      onClick={toggleVideo}
      onKeyDown={handleKeyDown}
      role='button'
      tabIndex={0}
      title={label}
    >
      {/* One transformed wrapper for both layers, so the poster and footage can never drift
          apart: it shrinks with the hover-crop and counter-shifts the card's drift 1:1. */}
      <div className='tw-absolute tw-inset-0 lg:-tw-inset-[5%] lg:tw-translate-x-[calc(var(--fm-dx)*-1)] lg:tw-translate-y-[calc(var(--fm-dy)*-1)] lg:tw-scale-[var(--fm-ms)]'>
        {/* Defer all media I/O to the first play: the required cover poster covers the wait, so
            preload="none" stops the section fetching every card's clip on scroll-in. Fall back to
            metadata only if the cover failed to resolve, so the card still shows a first frame. */}
        <video
          className='tw-absolute tw-inset-0 tw-size-full tw-object-cover'
          onEnded={handleStop}
          onPause={handleStop}
          onPlay={event => onPlay(event.currentTarget)}
          onPlaying={() => setIsShowingVideo(true)}
          playsInline
          preload={hasCover ? "none" : "metadata"}
          ref={videoRef}
          src={videoUrl}
        />
        {hasCover ? (
          <Image
            {...cover}
            className={clsx(
              "tw-pointer-events-none tw-absolute tw-inset-0 tw-transition-opacity tw-duration-300",
              isShowingVideo && "tw-opacity-0",
            )}
          />
        ) : null}
      </div>
      {/* lg-only: the inset math rides on the hover-crop vars and cqw (the container only
          exists at lg), and mobile cards have no crop. */}
      <div
        className='tw-pointer-events-none tw-absolute tw-hidden lg:tw-block lg:tw-backdrop-blur-[3px]'
        style={{
          inset: EDGE_BLUR_INSET,
          maskImage: EDGE_BLUR_MASK,
          WebkitMaskImage: EDGE_BLUR_MASK,
        }}
      />
      <PlayBadge
        currentTime={currentTime}
        duration={duration}
        isPlaying={isPlaying}
        progressAnimationStartedAt={progressAnimationStartedAt}
      />
    </div>
  );
}

// Shared PreviewPlayer control: icon state and progress ring track the element's real playback.
function PlayBadge({
  currentTime,
  duration,
  isPlaying,
  progressAnimationStartedAt,
}: {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  progressAnimationStartedAt: number | null;
}) {
  return (
    <div
      aria-hidden='true'
      // 6px inset = the 24px corner radius minus half the sm control's 36px box, centring the
      // button on the corner arc's centre. At lg the translate follows the hover-crop's visible
      // corner, riding the same per-frame smoothed --fm-t as the clip itself.
      className='tw-group/preview-button tw-pointer-events-auto tw-absolute tw-bottom-1.5 tw-right-1.5 tw-rounded-full tw-opacity-100 tw-transition-opacity tw-duration-300 tw-ease-out lg:tw-translate-x-[var(--fm-bx)] lg:tw-translate-y-[var(--fm-by)] lg:tw-opacity-0 lg:group-focus-within/floating-media-card:tw-opacity-100 lg:group-hover/floating-media-card:tw-opacity-100'
    >
      <PreviewPlayerButton isActive={isPlaying} size='sm'>
        <PreviewPlayerProgressRing
          currentTime={currentTime}
          duration={duration}
          isActive={isPlaying}
          progressAnimationStartedAt={progressAnimationStartedAt}
          size='sm'
        />
      </PreviewPlayerButton>
    </div>
  );
}
